import { WIZARD_PHASES, WizardPhaseKey } from '../../views/restaurant-onboarding/phases';
import { collectCompletedStepKeys, computeFirstIncompletePhaseKey, isPhaseComplete } from './onboardingWizardHelpers';

/**
 * Pure, dependency-free helpers for the restaurant-owner entry-point of the
 * partner two-actor onboarding flow (FOO-6351 C10). Kept separate from
 * `useOwnerSetupStatus` so the completion logic is unit-testable without the
 * React/api-client module graph (same split as `onboardingWizardHelpers`).
 */

/**
 * Only the step-backed phases the owner can do themselves gate their "Setting
 * up" entry-point. Phase 7 (Payouts/KYC) derives completion externally — gateway
 * verification can stay pending for days (plan §7) and is non-blocking for
 * go-live — so it must not keep the persistent "Setting up" card alive
 * indefinitely. Go Live is the partner's: counting it would leave the owner a
 * step they can never tick off.
 */
export const OWNER_SETUP_PHASES = WIZARD_PHASES.filter(
  (phase) => Boolean(phase.completionStepKey) && phase.key !== WizardPhaseKey.GoLive,
);

/**
 * Reduce the `GET /dashboard/onboarding-steps` response to the owner's setup
 * state: whether every step-backed phase is complete, and the first phase that
 * is not (the phase the "Continue setup" entry-point deep-links to).
 *
 * @param {Array} groups server onboarding-step groups
 * @returns {{ isComplete: boolean, firstIncompletePhaseKey: string, phases: Array, doneCount: number }}
 */
export function computeOwnerSetupStatus(groups) {
  const completedStepKeys = collectCompletedStepKeys(groups);

  const phases = OWNER_SETUP_PHASES.map((phase) => ({
    ...phase,
    isComplete: isPhaseComplete(phase, completedStepKeys),
  }));

  return {
    isComplete: phases.every((phase) => phase.isComplete),
    firstIncompletePhaseKey: computeFirstIncompletePhaseKey(phases),
    phases,
    doneCount: phases.filter((phase) => phase.isComplete).length,
  };
}
